import { useMemo, useCallback } from "react";
import { useForm, DefaultValues, Path } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { DateTime } from "luxon";
import { z } from "@/zod";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  CreateProjectScheduleForm,
  UpdateProjectScheduleForm,
} from "@/types/projectSchedule";
import { DEFAULT_DATE_FORMAT } from "@/constants";
import { useAsyncOperationProgress } from "@/components/progress/useAsyncOperationProgress";
import { BATCH_SIZE } from "../components/schedules/constants";
import { SCHEDULE_FORM_WEEKDAY_KEYS } from "@/schemas/scheduleFormWeekdays";

type WriteProjectScheduleForm =
  | CreateProjectScheduleForm
  | UpdateProjectScheduleForm;

interface UseProjectSchedulesUpsertFormWithBatchOptions<
  TFormData extends WriteProjectScheduleForm,
  TOutput,
> {
  startDate: Date;
  endDate: Date;
  schema: z.ZodSchema<TFormData>;
  additionalDefaultValues?: Partial<TFormData>;
  prepareData: (input: TFormData) => TOutput[];
  processBatch: (batch: TOutput[]) => Promise<unknown>;
  onSuccess: () => void;
  batchSize?: number;
}

const DEFAULT_ADDITIONAL_VALUES = {};

export const useProjectSchedulesUpsertFormWithBatch = <
  TFormData extends WriteProjectScheduleForm,
  TOutput,
>({
  startDate,
  endDate,
  schema,
  additionalDefaultValues = DEFAULT_ADDITIONAL_VALUES,
  prepareData,
  processBatch,
  onSuccess,
  batchSize = BATCH_SIZE,
}: UseProjectSchedulesUpsertFormWithBatchOptions<TFormData, TOutput>) => {
  const { t } = useTranslation();
  const operationProgress = useAsyncOperationProgress();

  const processBatched = useCallback(
    async (input: TFormData) => {
      // Prepare all data
      const allData = prepareData(input);

      // Split into batches of BATCH_SIZE
      // since we don't want to send a separate request for each item
      // and on the other hand we can't send them all in one request.
      const batches: TOutput[][] = [];
      for (let i = 0; i < allData.length; i += batchSize) {
        batches.push(allData.slice(i, i + batchSize));
      }

      // Initialize progress tracking with batch items
      const batchItems = batches.map((batch, index) => ({
        id: `batch-${index}`,
        label: t("projectSchedules.progress.batch", {
          number: index + 1,
          count: batch.length,
          defaultValue: `Batch ${index + 1} (${batch.length} schedules)`,
        }),
      }));

      operationProgress.initialize(batchItems);
      operationProgress.startProcessing();

      const errors: { batchIndex: number; error: Error }[] = [];

      // Process batches sequentially
      for (let i = 0; i < batches.length; i++) {
        if (operationProgress.isCancelling()) {
          break;
        }

        const batch = batches[i];
        const batchItem = batchItems[i];

        operationProgress.setCurrentItem(batchItem.label);

        try {
          await processBatch(batch);
          operationProgress.markItemProcessed(batchItem.id, true);
        } catch (error) {
          operationProgress.markItemProcessed(batchItem.id, false, error);
          errors.push({ batchIndex: i, error: error as Error });
        }
      }

      operationProgress.complete();

      return {
        totalBatches: batches.length,
        successfulBatches: batches.length - errors.length,
        failedBatches: errors.length,
        errors,
      };
    },
    [prepareData, processBatch, operationProgress, t, batchSize],
  );

  const mutation = useMutation({
    mutationFn: processBatched,
    onSuccess: () => {
      onSuccess();
      form.reset();
    },
    onError: () => {
      operationProgress.complete();
    },
  });

  const defaultValues: DefaultValues<TFormData> = useMemo(() => {
    const defaultData: WriteProjectScheduleForm = {
      hour_allocation_type: "visit",
      settlement_type: "hourly_rate",
      kam_planned_duration: "",
      date_start: DateTime.fromJSDate(startDate).toFormat(DEFAULT_DATE_FORMAT),
      date_end: DateTime.fromJSDate(endDate).toFormat(DEFAULT_DATE_FORMAT),
      suggested_start_time: "00:00",
      suggested_end_time: "23:59",
      weekdays: Object.fromEntries(
        SCHEDULE_FORM_WEEKDAY_KEYS.map((k) => [k, false]),
      ) as WriteProjectScheduleForm["weekdays"],
      rounding: false,
      every_second_week: false,
      ...additionalDefaultValues,
    };

    return defaultData as DefaultValues<TFormData>;
  }, [endDate, startDate, additionalDefaultValues]);

  const form = useForm<TFormData>({
    resolver: zodResolver(schema),
    defaultValues,
    disabled: mutation.isPending,
  });

  const settlementType = form.watch("settlement_type" as Path<TFormData>);

  const handleCancel = useCallback(() => {
    operationProgress.cancel();
  }, [operationProgress]);

  return {
    form,
    defaultValues,
    settlementType,
    mutation,
    operationProgress,
    handlers: {
      cancel: handleCancel,
    },
  };
};
